import { FC, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCountdown } from '@frontend/timer';
import { useDownloadFormPDF } from '../../../../../hooks/use-download-form-pdf';
import { useFillingExperienceContext } from '../../../provider/filling-experience.context';
import { deriveSubmissionConfirmationMode } from './derive-mode';
import { SubmissionConfirmationView } from './submission-confirmation.view';

export interface SubmissionConfirmationProps {
  /** Returned from the submit API response. Required for PDF download (undefined → download unavailable). */
  submissionId?: string;
}

const KIOSK_REDIRECT_DELAY_MS = 2000;
const COUNTDOWN_INITIAL_SECONDS = 120;

export const SubmissionConfirmation: FC<SubmissionConfirmationProps> = ({ submissionId }) => {
  const context = useFillingExperienceContext();
  const { locationId } = context;

  const isKioskOrigin = 'isKioskOrigin' in context ? context.isKioskOrigin : undefined;
  const isPreviewMode = 'isPreviewMode' in context ? context.isPreviewMode : undefined;

  const mode = deriveSubmissionConfirmationMode({
    experienceType: context.experienceType,
    isPreviewMode,
    isKioskOrigin,
  });

  const navigate = useNavigate();
  const kioskTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleKioskClose = useCallback(() => {
    if (kioskTimerRef.current) {
      clearTimeout(kioskTimerRef.current);
    }
    navigate({ to: `/kiosk/${locationId}` });
  }, [navigate, locationId]);

  // Auto-redirect kiosk to home after 2 s
  useEffect(() => {
    if (mode === 'kiosk') {
      kioskTimerRef.current = setTimeout(handleKioskClose, KIOSK_REDIRECT_DELAY_MS);
    }
    return () => {
      if (kioskTimerRef.current) {
        clearTimeout(kioskTimerRef.current);
      }
    };
  }, [mode, handleKioskClose]);

  const { count: countdownSeconds, isCompleted: isCountdownComplete } = useCountdown({
    initialValue: COUNTDOWN_INITIAL_SECONDS,
    autoStart: mode === undefined, // only run the timer for the Download flow
  });

  const { downloadPdf, isDownloading } = useDownloadFormPDF(locationId, submissionId ?? '');

  return (
    <SubmissionConfirmationView
      mode={mode}
      locationId={locationId}
      countdownSeconds={countdownSeconds}
      isCountdownComplete={isCountdownComplete}
      isDownloading={isDownloading}
      onDownloadPdf={downloadPdf}
      onKioskClose={handleKioskClose}
    />
  );
};
